import { useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import * as yup from "yup";
import { getInternalTeamValues, getTeamValues } from "../../../../api";
import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { useFormContext } from "../../../../hooks/useFormContext";
import { useFormField } from "../../../../hooks/useFormField";
import { useIsPreview } from "../../../../hooks/useIsPreview";
import { usePrefillContext } from "../../../../hooks/usePrefillContext";
import { Fields } from "../../../../types/fields";
import type { Team } from "../../../../types/form";
import { $t } from "../../../../utils/intl";
import type { FormFieldProps } from "../../../types";
import { validateWithYup } from "../../../validator";
import { validatorText } from "../../../validator.text";
import type { TeamFormValue } from "../team.config";
import { prefillTeam } from "../team.prefill";
import { TeamFieldMultiple } from "./TeamFieldMultiple";
import { TeamFieldSingle } from "./TeamFieldSingle";
import "./TeamFieldControl.sass";

export type TeamFieldControlProps = FormFieldProps;

export const TeamFieldControl: React.FunctionComponent<TeamFieldControlProps> = ({
  caption,
  field,
  formItem,
  label,
  name,
  required,
  isItemConditionSatisfied,
  readOnly,
}) => {
  const { isPreview } = useIsPreview();
  const requiredText = $t(validatorText.validationRequired);
  const formField = useFormField<Team[]>({
    name,
    validate: validateWithYup(
      yup
        .array()
        .of(yup.object())
        .when({
          is: () => formItem.required && !formItem.params.read_only && isItemConditionSatisfied,
          then: (at) => at.required(requiredText).min(1, requiredText),
        }),
    ),
  });

  const { accountId, config, passcode, isInternal, reportId } = useFormContext();

  const { data, isLoading } = useQuery(
    `${Fields.teamfield}_${field.slug}`,
    isInternal && reportId
      ? () => getInternalTeamValues(accountId, reportId, field.slug)
      : async () => {
          if (accountId && config.sharing_hash && field.slug) {
            return await getTeamValues(
              accountId,
              config.sharing_hash,
              field.slug,
              isPreview,
              passcode,
            );
          }
        },
    { staleTime: Infinity },
  );

  const options = useMemo<TeamFormValue[]>(
    () =>
      (data ?? [])
        .sort((t1, t2) => t1.name.toLowerCase().localeCompare(t2.name.toLowerCase()))
        .map((team) => ({
          id: team.id,
          name: team.name,
          color: team.color,
          team,
        })),
    [data],
  );

  const { prefillValues } = usePrefillContext();
  const prefillValue = prefillValues.dynamicFields[name];

  const handleSelectChange = (changeValue: Team | Team[]): void => {
    if (changeValue) {
      if (Array.isArray(changeValue)) {
        formField.onChange(changeValue);
      } else {
        formField.onChange([changeValue]);
      }
    } else {
      formField.onChange([]);
    }
  };

  useEffect(() => {
    if (prefillValue && !isLoading && formField.value === undefined) {
      const parsedPrefillValue = prefillTeam({
        value: prefillValue,
        field,
        members: (data ?? []).map((t) => ({ id: t.id, name: t.name })),
      });
      handleSelectChange(parsedPrefillValue as Team[]);
    }
  }, [prefillValue, isLoading]);

  const isMultiple = field.params?.entries_allowed === "multiple";

  if (!isItemConditionSatisfied) return null;

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      caption={caption}
      errorMessage={formField.errorMessage}
      label={label}
      helpText={formItem.params.helpText}
      required={required}
      state={formField.state}
      readOnly={readOnly}
    >
      {isMultiple ? (
        <TeamFieldMultiple
          options={options}
          formField={formField}
          readOnly={readOnly}
          isLoading={isLoading}
        />
      ) : (
        <TeamFieldSingle
          options={options}
          formField={formField}
          readOnly={readOnly}
          isLoading={isLoading}
          onChange={handleSelectChange}
        />
      )}
    </FormFieldControl>
  );
};
